import { Grid, Drawer, Card, Typography, Tag, Divider, Space, Avatar } from '@pankod/refine-antd';
import { IVariant } from 'services/variant';
import { capitalize } from 'utilities/shared';
import defaultAvatar from 'assets/images/default-avatar.jpg';
import { useCan } from '@pankod/refine-core';

interface ProductVariantShowDrawerProps {
  variant: IVariant | null;
  visible: boolean;
  onClose: () => void;
}

export const ProductVariantShowDrawer: React.FC<ProductVariantShowDrawerProps> = ({
  variant,
  visible,
  onClose,
}) => {
  const breakpoint = Grid.useBreakpoint();
  const drawerWidth = breakpoint.lg ? 520 : breakpoint.md ? 460 : '100%';
  const avatarSize = breakpoint.md ? 250 : 180;
  const { data: readPurchasePrice } = useCan({
    resource: 'purchase_price',
    action: 'read',
  });

  const attributes: {
    attributeId: string;
    attributeName: string;
    attributeType: 'text' | 'float' | 'boolean' | 'dropdown';
    value: string;
  }[] = [];
  const templates = variant?.productTemplates || [];
  templates.forEach((template) => {
    const { productTemplateAttributes } = template;
    attributes.push(...productTemplateAttributes);
  });

  const customFields = variant?.customFields ?? [];

  return (
    <Drawer
      placement="right"
      width={drawerWidth}
      bodyStyle={{ padding: breakpoint.sm ? 24 : 16 }}
      visible={visible}
      onClose={onClose}
    >
      {variant && (
        <>
          <Space
            direction="vertical"
            align="center"
            style={{
              width: '100%',
              textAlign: 'center',
              margin: '26px 0 30px 0',
            }}
          >
            <Avatar
              src={variant?.productVariantPhoto?.url || defaultAvatar}
              size={avatarSize}
              style={{
                alignItems: 'center',
                objectFit: 'cover',
              }}
              alt={`Avatar`}
            />
          </Space>

          <Divider />

          <Card title="Info">
            <Typography.Title level={5}>Name</Typography.Title>
            <Typography.Text>{variant.productVariantName}</Typography.Text>

            <Typography.Title level={5}>SKU</Typography.Title>
            <Typography.Text>{variant.sku}</Typography.Text>

            <Typography.Title level={5}>Description</Typography.Title>
            <Typography.Text>{variant.description}</Typography.Text>

            {readPurchasePrice?.can && (
              <>
                <Typography.Title level={5}>Default Purchase Price</Typography.Title>
                <Typography.Text>{variant.purchasePrice}</Typography.Text>
              </>
            )}

            <Typography.Title level={5}>Default Selling Price</Typography.Title>
            <Typography.Text>{variant.sellingPrice}</Typography.Text>

            <Typography.Title level={5}>Templates</Typography.Title>
            <Typography.Text>
              {variant.productTemplates.map((item: any) => (
                <Tag key={item.templateId}>{item.templateName}</Tag>
              ))}
            </Typography.Text>

            <Typography.Title level={5}>Product Name</Typography.Title>
            <Typography.Text>{variant.productName}</Typography.Text>

            <Typography.Title level={5}>Category</Typography.Title>
            <Typography.Text>
              {variant.category ? (
                <Tag key={variant.category.id} color="blue">
                  {variant.category.name}
                </Tag>
              ) : null}
            </Typography.Text>

            <Typography.Title level={5}>Tags</Typography.Title>
            <Typography.Text>
              {variant.tags.map((item: { id: string; name: string }) => (
                <Tag key={item.id}>{item.name}</Tag>
              ))}
            </Typography.Text>

            <Typography.Title level={5}>Warehouses</Typography.Title>
            <Typography.Text>
              {(variant?.warehouses ?? []).map((item: { id: string; name: string }) => (
                <Tag key={item.id}>{item.name}</Tag>
              ))}
            </Typography.Text>
          </Card>

          <Divider />

          {attributes.map((attribute) => (
            <Card key={attribute.attributeId} style={{ marginBottom: '1rem' }}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  flexWrap: 'wrap',
                  wordBreak: 'break-word',
                }}
              >
                <Typography.Text strong>{attribute.attributeName.toUpperCase()} : </Typography.Text>
                <Typography.Text>{capitalize(attribute.value)}</Typography.Text>
              </div>
            </Card>
          ))}

          {customFields.map((field, idx) => {
            const key = Object.keys(field)[0];
            const value = field[key];

            return (
              <Card key={idx} style={{ marginBottom: '1rem' }}>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    flexWrap: 'wrap',
                    wordBreak: 'break-word',
                  }}
                >
                  <Typography.Text strong>{key.toUpperCase()} : </Typography.Text>
                  <Typography.Text>{capitalize(value)}</Typography.Text>
                </div>
              </Card>
            );
          })}
        </>
      )}
    </Drawer>
  );
};
